import { useState, useEffect } from 'react'
import { i18n } from '@fs/zion-locale'

// Import font files directly
import surnamesKaitiScRegularWoff from '../../assets/fonts/icomoon/surnames-kaiti-sc-regular/fonts/surnames-kaiti-sc-regular.woff'
import surnamesKaitiScRegularTtf from '../../assets/fonts/icomoon/surnames-kaiti-sc-regular/fonts/surnames-kaiti-sc-regular.ttf'

import surnamesKaitiTcRegularWoff from '../../assets/fonts/icomoon/surnames-kaiti-tc-regular/fonts/surnames-kaiti-tc-regular.woff'
import surnamesKaitiTcRegularTtf from '../../assets/fonts/icomoon/surnames-kaiti-tc-regular/fonts/surnames-kaiti-tc-regular.ttf'

const surnamesFontFamilyScRegular = 'SurnamesKaitiSC'
const surnamesFontFamilyTcRegular = 'SurnamesKaitiTC'

// Ensure @font-face is available to print stylesheets
const ensureFontFaceStyle = (fontFamily, woffUrl, ttfUrl) => {
  if (typeof document === 'undefined') {
    return
  }

  const existingTag = document.querySelector(`style[data-font-family="${fontFamily}"]`)

  if (existingTag) {
    return
  }

  const styleTag = document.createElement('style')
  styleTag.dataset.fontFamily = fontFamily
  styleTag.appendChild(
    document.createTextNode(`@font-face {
      font-family: '${fontFamily}';
      src: url('${woffUrl}') format('woff'), url('${ttfUrl}') format('truetype');
      font-style: normal;
      font-weight: 400;
      font-display: swap;
    }`)
  )

  document.head.appendChild(styleTag)
}

// Create font face definitions
const createFontFace = (fontFamily, woffUrl, ttfUrl) => {
  // woff and ttf formats for broader browser support. The woff format is older and more widely supported while ttf is a universal fallback.
  const fontFace = new FontFace(
    fontFamily,
    `
    url("${woffUrl}") format("woff"),
    url("${ttfUrl}") format("truetype")
  `,
    {
      style: 'normal',
      weight: 'normal',
      display: 'swap',
    }
  )

  return fontFace
}

export default function useFonts() {
  const [regularKaiti, setRegularKaiti] = useState(surnamesFontFamilyTcRegular)
  const [isFontLoaded, setIsFontLoaded] = useState(false)

  useEffect(() => {
    let isMounted = true

    const loadFont = async () => {
      try {
        let fontFace

        if (i18n.language === 'zh-hans') {
          ensureFontFaceStyle(surnamesFontFamilyScRegular, surnamesKaitiScRegularWoff, surnamesKaitiScRegularTtf)
          fontFace = createFontFace(surnamesFontFamilyScRegular, surnamesKaitiScRegularWoff, surnamesKaitiScRegularTtf)
          if (isMounted) {
            setRegularKaiti(surnamesFontFamilyScRegular)
          }
        } else {
          ensureFontFaceStyle(surnamesFontFamilyTcRegular, surnamesKaitiTcRegularWoff, surnamesKaitiTcRegularTtf)
          fontFace = createFontFace(surnamesFontFamilyTcRegular, surnamesKaitiTcRegularWoff, surnamesKaitiTcRegularTtf)
          if (isMounted) {
            setRegularKaiti(surnamesFontFamilyTcRegular)
          }
        }

        // Load the font
        await fontFace.load()

        // Add to document fonts
        document.fonts.add(fontFace)

        if (isMounted) {
          setIsFontLoaded(true)
        }
      } catch (error) {
        console.error('Error loading font:', error)
        // Set as loaded to prevent indefinite loading state and allow fallback fonts
        if (isMounted) {
          setIsFontLoaded(true)
        }
      }
    }

    loadFont()

    return () => {
      isMounted = false
    }
  }, [])

  return { regularKaiti, isFontLoaded }
}
